import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

const C = {
  navy: '#0e225d',
  navyMid: '#1a3370',
  navyHover: 'rgba(14,34,93,0.06)',
  gold: '#fbb21f',
  goldDim: 'rgba(251,178,31,0.14)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff',
  border: '#e5e7eb',
  muted: '#9ca3af',
  sub: '#6b7280',
  body: '#1f2937',
  bg: '#f7f8fa',
  green: '#16a34a',
  greenDim: 'rgba(22,163,74,0.12)',
  greenBorder: 'rgba(22,163,74,0.3)',
  red: '#dc2626',
  redDim: 'rgba(220,38,38,0.08)',
  redBorder: 'rgba(220,38,38,0.25)',
};

/* ─── Types ─────────────────────────────────────────────────────────── */
interface AttemptQuestion {
  question_id:   string; // index-based id, as string
  question_text: string;
  options:       string[];
  marks:         number;
}

interface AttemptExam {
  class_test_id:    string;
  title:            string;
  subject:          string;
  total_marks:      number;
  duration_minutes: number;
  questions:        AttemptQuestion[];
}

interface SubmitResult {
  obtained:        number;
  total:           number;
  answered:        number;
  skipped:         number;
  total_questions: number;
}

interface AttemptStatusResult {
  obtained:        number;
  total:           number;
  answered:        number;
  skipped:         number;
  total_questions: number;
  submitted_at:    string | null;
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

const fmtTime = (secs: number): string => {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const TakeExam: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  /* ── Load / gating state ────────────────────────────────────────── */
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState<string | null>(null);
  const [exam,    setExam]      = useState<AttemptExam | null>(null);

  // If already attempted (checked via attempt-status before loading questions)
  const [alreadyAttempted, setAlreadyAttempted] = useState(false);
  const [priorResult, setPriorResult] = useState<AttemptStatusResult | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);

  /* ── Exam-taking state ──────────────────────────────────────────── */
  const [currentIdx, setCurrentIdx]   = useState<number>(0);
  const [answers, setAnswers]         = useState<Record<string, string>>({});
  const [flags, setFlags]             = useState<Set<string>>(new Set());
  const [timeLeft, setTimeLeft]       = useState<number>(0);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [submitted, setSubmitted]     = useState<boolean>(false);
  const [submitting, setSubmitting]   = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult]           = useState<SubmitResult | null>(null);

  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const answersRef  = useRef<Record<string, string>>(answers);

  useEffect(() => { answersRef.current = answers; }, [answers]);

  /* ── Step 1: check attempt status, then load questions if eligible ── */
  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        setLoading(true);
        setError(null);

        const statusRes = await apiClient.get(`/v1/class-tests/${id}/attempt-status`);
        const status = statusRes.data?.data;

        if (status?.attempted) {
          setAlreadyAttempted(true);
          setPriorResult(status.result ?? null);
          setLoading(false);
          return;
        }

        if (!status?.can_attempt) {
          setUnavailable('This test is not currently available.');
          setLoading(false);
          return;
        }

        // Eligible — load questions
        const attemptRes = await apiClient.get(`/v1/class-tests/${id}/attempt`);
        if (attemptRes.data.success) {
          const data: AttemptExam = attemptRes.data.data;
          setExam(data);
          setTimeLeft((data.duration_minutes || 30) * 60);
        } else {
          setError('Failed to load test');
        }
      } catch (err: any) {
        const apiError = err?.response?.data?.error;
        if (err?.response?.status === 403 && apiError) {
          if (err.response.data?.attempted) {
            setAlreadyAttempted(true);
          } else {
            setUnavailable(apiError);
          }
        } else {
          setError(apiError || 'Failed to load test');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const questions = exam?.questions ?? [];

  /* ── Timer ──────────────────────────────────────────────────────── */
  const handleAutoSubmit = useCallback(() => {
    doSubmit(answersRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!exam || submitted) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exam, submitted]);

  /* ── Answer helpers ─────────────────────────────────────────────── */
  const goTo = useCallback((idx: number) => {
    setCurrentIdx(idx);
  }, []);

  const setAnswer = (qid: string, value: string) =>
    setAnswers(prev => ({ ...prev, [qid]: value }));

  const clearAnswer = (qid: string) => {
    setAnswers(prev => { const n = { ...prev }; delete n[qid]; return n; });
  };

  const toggleFlag = (qid: string) => {
    setFlags(prev => {
      const n = new Set(prev);
      n.has(qid) ? n.delete(qid) : n.add(qid);
      return n;
    });
  };

  /* ── Submit ─────────────────────────────────────────────────────── */
  const doSubmit = async (finalAnswers: Record<string, string>) => {
    if (!id || submitting || submitted) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await apiClient.post(`/v1/class-tests/${id}/submit`, { answers: finalAnswers });
      if (res.data.success) {
        const d = res.data.data;
        setResult({
          obtained: d.obtained,
          total: d.total,
          answered: d.answered,
          skipped: d.skipped,
          total_questions: d.total_questions,
        });
        setSubmitted(true);
        if (timerRef.current) clearInterval(timerRef.current);
      } else {
        setSubmitError('Failed to submit. Please try again.');
      }
    } catch (err: any) {
      const apiError = err?.response?.data?.error;
      if (err?.response?.status === 403 && apiError) {
        // Likely already submitted (e.g. duplicate tab) — treat as success-ish
        setAlreadyAttempted(true);
        setExam(null);
      } else {
        setSubmitError(apiError || 'Failed to submit. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    setShowConfirm(false);
    doSubmit(answersRef.current);
  };

  const isWarning  = timeLeft <= 300 && timeLeft > 0;
  const isCritical = timeLeft <= 60  && timeLeft > 0;

  const answeredCount = Object.keys(answers).length;
  const unanswered    = questions.length - answeredCount;

  const dotStyle = (idx: number): React.CSSProperties => {
    const qid   = questions[idx].question_id;
    const isCur = idx === currentIdx;
    const ans   = answers[qid] !== undefined;
    const flag  = flags.has(qid);
    if (isCur) return { background: C.navy,     color: C.white,   borderColor: C.navy };
    if (flag)  return { background: '#fffbeb',  color: '#92400e', borderColor: '#fcd34d' };
    if (ans)   return { background: C.greenDim, color: '#166534', borderColor: '#86efac' };
    return       { background: C.white, color: C.sub, borderColor: C.border };
  };

  /* ── LOADING ──────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.95rem', fontWeight: '500' }}>Loading test…</p>
        </div>
      </div>
    );
  }

  /* ── ERROR ────────────────────────────────────────────────────────── */
  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem', width: '100%' }} />
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
        >
          <i className="pi pi-arrow-left" /> Go Back
        </button>
      </div>
    );
  }

  /* ── UNAVAILABLE (not scheduled / not open) ─────────────────────────── */
  if (unavailable) {
    return (
      <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '52px 26px', textAlign: 'center' }}>
        <div style={{ width: '70px', height: '70px', borderRadius: '35px', background: '#E8EEF7', border: `2px solid ${C.gold}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <i className="pi pi-lock" style={{ fontSize: '1.6rem', color: C.navy }} />
        </div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: C.navy }}>Test Unavailable</h3>
        <p style={{ margin: '0 0 20px 0', color: C.sub, fontSize: '0.88rem' }}>{unavailable}</p>
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 22px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
        >
          <i className="pi pi-arrow-left" /> Go Back
        </button>
      </div>
    );
  }

  /* ── ALREADY ATTEMPTED ────────────────────────────────────────────── */
  if (alreadyAttempted && !submitted) {
    const pr = priorResult;
    const pct = pr && pr.total > 0 ? Math.round((pr.obtained / pr.total) * 100) : null;
    return (
      <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '52px 26px', textAlign: 'center' }}>
        <div style={{ width: '70px', height: '70px', borderRadius: '35px', background: C.greenDim, border: `2px solid ${C.greenBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <i className="pi pi-check-circle" style={{ fontSize: '1.6rem', color: C.green }} />
        </div>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: C.navy }}>Already Attempted</h3>
        <p style={{ margin: '0 0 20px 0', color: C.sub, fontSize: '0.88rem' }}>
          You've already submitted this test{pr?.submitted_at ? ` on ${pr.submitted_at.split(' ')[0]}` : ''}.
        </p>
        {pr && (
          <div style={{ display: 'inline-flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <div style={{ background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '10px', padding: '10px 18px', textAlign: 'center', minWidth: '90px' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#a06000' }}>{pr.obtained} / {pr.total}</div>
              <div style={{ fontSize: '0.7rem', color: '#a06000', fontWeight: '600', textTransform: 'uppercase', marginTop: '4px' }}>Score{pct !== null ? ` (${pct}%)` : ''}</div>
            </div>
            <div style={{ background: C.greenDim, border: `1px solid ${C.greenBorder}`, borderRadius: '10px', padding: '10px 18px', textAlign: 'center', minWidth: '90px' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: '800', color: C.green }}>{pr.answered}</div>
              <div style={{ fontSize: '0.7rem', color: C.green, fontWeight: '600', textTransform: 'uppercase', marginTop: '4px' }}>Answered</div>
            </div>
            <div style={{ background: C.redDim, border: `1px solid ${C.redBorder}`, borderRadius: '10px', padding: '10px 18px', textAlign: 'center', minWidth: '90px' }}>
              <div style={{ fontSize: '1.2rem', fontWeight: '800', color: C.red }}>{pr.skipped}</div>
              <div style={{ fontSize: '0.7rem', color: C.red, fontWeight: '600', textTransform: 'uppercase', marginTop: '4px' }}>Skipped</div>
            </div>
          </div>
        )}
        <div>
          <button
            onClick={() => navigate(-1)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 22px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
          >
            <i className="pi pi-arrow-left" /> Go Back
          </button>
        </div>
      </div>
    );
  }

  /* ── RESULT SCREEN (just submitted) ─────────────────────────────────── */
  if (submitted && result && exam) {
    const pct    = result.total > 0 ? Math.round((result.obtained / result.total) * 100) : 0;
    const passed = pct >= 40;
    const radius = 52;
    const circ   = 2 * Math.PI * radius;
    const dash   = (pct / 100) * circ;

    return (
      <div style={{ background: C.bg, minHeight: '100%', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '28px 32px', boxShadow: '0 1px 8px rgba(14,34,93,0.07)', display: 'flex', alignItems: 'center', gap: '32px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', width: '130px', height: '130px', flexShrink: 0 }}>
            <svg width="130" height="130" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="65" cy="65" r={radius} fill="none" stroke={C.border} strokeWidth="10" />
              <circle cx="65" cy="65" r={radius} fill="none" stroke={passed ? C.green : C.red} strokeWidth="10"
                strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '1.6rem', fontWeight: '800', color: passed ? C.green : C.red, lineHeight: 1 }}>{pct}%</span>
              <span style={{ fontSize: '0.72rem', fontWeight: '700', color: passed ? C.green : C.red, marginTop: '3px' }}>{passed ? 'PASSED' : 'FAILED'}</span>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: '800', color: C.navy, marginBottom: '14px' }}>{exam.title}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {[
                { lbl: 'Score',    val: `${result.obtained} / ${result.total}`, bg: C.goldDim,  border: C.goldBorder,  color: '#a06000' },
                { lbl: 'Answered', val: String(result.answered),                bg: C.greenDim, border: C.greenBorder, color: C.green },
                { lbl: 'Skipped',  val: String(result.skipped),                 bg: C.redDim,   border: C.redBorder,   color: C.red },
              ].map(({ lbl, val, bg, border, color }) => (
                <div key={lbl} style={{ background: bg, border: `1px solid ${border}`, borderRadius: '10px', padding: '10px 16px', textAlign: 'center', minWidth: '80px' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: '800', color, lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: '0.7rem', color, marginTop: '4px', fontWeight: '600', textTransform: 'uppercase' }}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, fontWeight: '700', fontSize: '0.9rem', color: C.navy }}>
            Answer Review
          </div>
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {questions.map((q, i) => {
              const skipped     = answers[q.question_id] === undefined;
              const borderColor = skipped ? C.border : C.greenBorder;
              const bgColor     = skipped ? C.white  : C.greenDim;
              const icon        = skipped ? 'pi-minus-circle' : 'pi-check-circle';
              const iconColor   = skipped ? C.muted : C.green;
              return (
                <div key={q.question_id} style={{ border: `1px solid ${borderColor}`, borderRadius: '10px', padding: '12px 16px', background: bgColor }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <i className={`pi ${icon}`} style={{ fontSize: '1rem', color: iconColor, flexShrink: 0, marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: '700', background: 'rgba(14,34,93,0.08)', color: C.navy, padding: '2px 9px', borderRadius: '20px' }}>Q {i + 1}</span>
                        <span style={{ fontSize: '0.7rem', color: iconColor, fontWeight: '700' }}>{skipped ? 'Skipped' : 'Answered'}</span>
                      </div>
                      <p style={{ fontSize: '0.85rem', fontWeight: '600', color: C.body, lineHeight: 1.5, marginBottom: '6px' }}>{q.question_text}</p>
                      <div style={{ fontSize: '0.78rem', color: C.sub }}>
                        Your answer: <strong style={{ color: skipped ? C.muted : C.navy }}>{skipped ? '—' : answers[q.question_id]}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <button
            onClick={() => navigate(-1)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 22px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
          >
            <i className="pi pi-arrow-left" /> Back to Class Tests
          </button>
        </div>
      </div>
    );
  }

  /* ── EXAM SCREEN ── */
  if (!exam || questions.length === 0) {
    return (
      <div style={{ background: C.white, borderRadius: '16px', border: `1px solid ${C.border}`, padding: '52px 26px', textAlign: 'center' }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: C.navy }}>No Questions Available</h3>
        <p style={{ margin: '0 0 20px 0', color: C.sub, fontSize: '0.88rem' }}>This test doesn't have any questions yet.</p>
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 22px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
        >
          <i className="pi pi-arrow-left" /> Go Back
        </button>
      </div>
    );
  }

  const question = questions[currentIdx];

  return (
    <>
      <style>{`
        .ep-opt:hover { border-color:${C.gold} !important; background:${C.goldDim} !important; }
        .ep-dot:hover { opacity:0.8; }
        .ep-nav:hover:not(:disabled) { border-color:${C.navy} !important; color:${C.navy} !important; }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: C.bg }}>

        {/* TOP BAR */}
        <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '0 20px', height: '58px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: '12px' }}>
          <div>
            <div style={{ fontWeight: '700', fontSize: '0.92rem', color: C.navy }}>{exam.title}</div>
            <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: '1px' }}>
              {exam.subject} &nbsp;·&nbsp; {questions.length} Questions &nbsp;·&nbsp; {exam.total_marks} Marks
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 16px', borderRadius: '9px', background: isCritical ? C.redDim : isWarning ? '#fffbeb' : C.navyHover, border: `1px solid ${isCritical ? C.redBorder : isWarning ? '#fcd34d' : 'rgba(14,34,93,0.12)'}` }}>
            <i className="pi pi-clock" style={{ fontSize: '0.9rem', color: isCritical ? C.red : isWarning ? '#92400e' : C.navy }} />
            <div>
              <div style={{ fontSize: '10px', fontWeight: '600', color: isCritical ? C.red : isWarning ? '#92400e' : C.muted, lineHeight: 1 }}>
                {isCritical ? 'HURRY UP!' : 'TIME LEFT'}
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: '800', color: isCritical ? C.red : isWarning ? '#92400e' : C.navy, fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                {fmtTime(timeLeft)}
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowConfirm(true)}
            disabled={submitting}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', background: C.navy, border: 'none', borderRadius: '9px', color: C.white, fontSize: '0.83rem', fontWeight: '700', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1, boxShadow: '0 2px 8px rgba(14,34,93,0.25)', transition: 'all 0.2s' }}
            onMouseEnter={(e) => { if (!submitting) { e.currentTarget.style.background = C.navyMid; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
            onMouseLeave={(e) => { e.currentTarget.style.background = C.navy; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <i className="pi pi-check-circle" style={{ fontSize: '0.88rem' }} /> Submit Exam
          </button>
        </div>

        {submitError && (
          <div style={{ padding: '10px 20px' }}>
            <Message severity="error" text={submitError} style={{ width: '100%', fontSize: '0.85rem' }} />
          </div>
        )}

        {/* BODY */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Question Area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '0.78rem', color: C.sub, whiteSpace: 'nowrap' }}>Question {currentIdx + 1} of {questions.length}</span>
              <div style={{ flex: 1, height: '5px', background: C.border, borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: C.gold, borderRadius: '3px', width: `${((currentIdx + 1) / questions.length) * 100}%`, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: '0.78rem', color: C.sub, whiteSpace: 'nowrap' }}>{answeredCount} answered</span>
            </div>

            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '22px 24px', boxShadow: '0 1px 4px rgba(14,34,93,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <span style={{ background: C.navy, color: C.white, fontSize: '0.72rem', fontWeight: '700', padding: '3px 11px', borderRadius: '20px' }}>Q {currentIdx + 1}</span>
                <span style={{ background: C.goldDim, color: '#a06000', border: `1px solid ${C.goldBorder}`, fontSize: '0.72rem', fontWeight: '700', padding: '3px 11px', borderRadius: '20px' }}>
                  {question.marks} {question.marks === 1 ? 'Mark' : 'Marks'}
                </span>
                {flags.has(question.question_id) && (
                  <span style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d', fontSize: '0.72rem', fontWeight: '700', padding: '3px 11px', borderRadius: '20px' }}>
                    ⚑ For Review
                  </span>
                )}
              </div>

              <p style={{ fontSize: '0.95rem', fontWeight: '600', color: C.body, lineHeight: 1.65, marginBottom: '22px' }}>
                {question.question_text}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                {question.options.map((opt, i) => {
                  const selected = answers[question.question_id] === opt;
                  return (
                    <button
                      key={i}
                      className="ep-opt"
                      onClick={() => setAnswer(question.question_id, opt)}
                      style={{ display: 'flex', alignItems: 'center', gap: '13px', padding: '11px 16px', textAlign: 'left', border: selected ? `1.5px solid ${C.gold}` : `1.5px solid ${C.border}`, borderRadius: '10px', cursor: 'pointer', background: selected ? C.goldDim : C.white, transition: 'all 0.15s', width: '100%' }}
                    >
                      <div style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: '700', background: selected ? C.gold : 'transparent', border: selected ? `1.5px solid ${C.gold}` : `1.5px solid ${C.border}`, color: selected ? C.navy : C.muted }}>
                        {question.options.length === 2 && question.options[0] === 'True' && question.options[1] === 'False'
                          ? (i === 0 ? 'T' : 'F')
                          : OPTION_LETTERS[i]}
                      </div>
                      <span style={{ fontSize: '0.88rem', color: selected ? C.body : C.sub, fontWeight: selected ? '600' : '400' }}>{opt}</span>
                      {selected && <i className="pi pi-check" style={{ marginLeft: 'auto', fontSize: '0.78rem', color: C.gold }} />}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <button
                className="ep-nav"
                onClick={() => goTo(Math.max(0, currentIdx - 1))}
                disabled={currentIdx === 0}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', border: `1.5px solid ${C.border}`, borderRadius: '9px', background: C.white, color: C.sub, fontSize: '0.83rem', fontWeight: '600', cursor: currentIdx === 0 ? 'not-allowed' : 'pointer', opacity: currentIdx === 0 ? 0.4 : 1, transition: 'all 0.15s' }}
              >
                <i className="pi pi-arrow-left" style={{ fontSize: '0.8rem' }} /> Previous
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {answers[question.question_id] !== undefined && (
                  <button
                    onClick={() => clearAnswer(question.question_id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '9px 14px', border: `1.5px solid ${C.redBorder}`, borderRadius: '9px', background: C.redDim, color: C.red, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
                  >
                    <i className="pi pi-times" style={{ fontSize: '0.75rem' }} /> Clear
                  </button>
                )}
                <button
                  onClick={() => toggleFlag(question.question_id)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: flags.has(question.question_id) ? '1.5px solid #fcd34d' : `1.5px solid ${C.border}`, borderRadius: '9px', background: flags.has(question.question_id) ? '#fffbeb' : C.white, color: flags.has(question.question_id) ? '#92400e' : C.sub, fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' }}
                >
                  <i className="pi pi-flag" style={{ fontSize: '0.8rem' }} />
                  {flags.has(question.question_id) ? 'Marked' : 'Mark for Review'}
                </button>
              </div>

              <button
                className="ep-nav"
                onClick={() => goTo(Math.min(questions.length - 1, currentIdx + 1))}
                disabled={currentIdx === questions.length - 1}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', border: `1.5px solid ${C.border}`, borderRadius: '9px', background: C.white, color: C.sub, fontSize: '0.83rem', fontWeight: '600', cursor: currentIdx === questions.length - 1 ? 'not-allowed' : 'pointer', opacity: currentIdx === questions.length - 1 ? 0.4 : 1, transition: 'all 0.15s' }}
              >
                Next <i className="pi pi-arrow-right" style={{ fontSize: '0.8rem' }} />
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ width: '240px', borderLeft: `1px solid ${C.border}`, background: C.white, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', flexShrink: 0 }}>

            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted, marginBottom: '8px' }}>Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px' }}>
                {[
                  { num: answeredCount,   lbl: 'Answered' },
                  { num: unanswered,      lbl: 'Skipped' },
                  { num: flags.size,      lbl: 'For Review' },
                  { num: questions.length, lbl: 'Total' },
                ].map(({ num, lbl }) => (
                  <div key={lbl} style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 10px' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: '800', color: C.navy, lineHeight: 1 }}>{num}</div>
                    <div style={{ fontSize: '10px', color: C.muted, marginTop: '3px' }}>{lbl}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted, marginBottom: '8px' }}>Question Palette</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '5px' }}>
                {questions.map((q, i) => (
                  <button key={q.question_id} className="ep-dot" onClick={() => goTo(i)}
                    style={{ width: '36px', height: '36px', borderRadius: '8px', border: '1.5px solid', fontSize: '11px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.12s', ...dotStyle(i) }}>
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: C.muted, marginBottom: '8px' }}>Legend</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {[
                  { bg: C.navy,     border: C.navy,    label: 'Current' },
                  { bg: C.greenDim, border: '#86efac', label: 'Answered' },
                  { bg: '#fffbeb',  border: '#fcd34d', label: 'For Review' },
                  { bg: C.white,    border: C.border,  label: 'Not Visited' },
                ].map(({ bg, border, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11px', color: C.sub }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: bg, border: `1.5px solid ${border}`, flexShrink: 0 }} />
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Confirm Overlay */}
      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: C.white, borderRadius: '16px', padding: '28px', width: '400px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: '800', fontSize: '1rem', color: C.navy, marginBottom: '18px' }}>Submit Exam?</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginBottom: '18px' }}>
              {[
                { num: answeredCount, lbl: 'Answered', bg: C.greenDim, border: C.greenBorder, color: C.green },
                { num: unanswered,    lbl: 'Skipped',  bg: C.redDim,   border: C.redBorder,   color: C.red },
                { num: flags.size,    lbl: 'Review',   bg: '#fffbeb',  border: '#fcd34d',     color: '#92400e' },
              ].map(({ num, lbl, bg, border, color }) => (
                <div key={lbl} style={{ background: bg, border: `1px solid ${border}`, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color, lineHeight: 1 }}>{num}</div>
                  <div style={{ fontSize: '0.7rem', color, marginTop: '4px', fontWeight: '600' }}>{lbl}</div>
                </div>
              ))}
            </div>

            {unanswered > 0 && (
              <div style={{ display: 'flex', gap: '10px', padding: '12px 14px', background: C.redDim, border: `1px solid ${C.redBorder}`, borderRadius: '9px', marginBottom: '18px' }}>
                <i className="pi pi-exclamation-triangle" style={{ color: C.red, flexShrink: 0 }} />
                <p style={{ fontSize: '0.82rem', color: C.red, fontWeight: '500', margin: 0, lineHeight: 1.5 }}>
                  {unanswered} question{unanswered > 1 ? 's' : ''} unanswered. You cannot change answers after submitting.
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowConfirm(false)}
                disabled={submitting}
                style={{ padding: '9px 18px', border: `1.5px solid ${C.border}`, borderRadius: '9px', background: C.white, color: C.sub, fontSize: '0.83rem', fontWeight: '600', cursor: submitting ? 'not-allowed' : 'pointer' }}
              >
                Continue Exam
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', background: C.navy, border: 'none', borderRadius: '9px', color: C.white, fontSize: '0.83rem', fontWeight: '700', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1, boxShadow: '0 2px 8px rgba(14,34,93,0.25)' }}
              >
                {submitting ? (
                  <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" />
                ) : (
                  <i className="pi pi-check-circle" style={{ fontSize: '0.85rem' }} />
                )}
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TakeExam;